import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import AuthGuard from '../components/auth/AuthGuard';
import AppShell from '../components/layout/AppShell';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PermissionGuard from '../components/auth/PermissionGuard';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../store/auth';
import { useCompanyStore } from '../store/company';
import { rememberRoute, resolveAuthenticatedPath } from '../lib/postLoginPath';

// Lazy loading para rotas pesadas
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const CompaniesPage = React.lazy(() => import('../pages/CompaniesPage'));
const CompanyUsersPage = React.lazy(() => import('../pages/CompanyUsersPage'));
const RolesPage = React.lazy(() => import('../pages/RolesPage'));
const RoleEditPage = React.lazy(() => import('../pages/RoleEditPage'));
const InvitationsPage = React.lazy(() => import('../pages/InvitationsPage'));
const InviteAcceptPage = React.lazy(() => import('../pages/InviteAcceptPage'));
const ForgotPasswordPage = React.lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/ResetPasswordPage'));
const EmailVerifiedPage = React.lazy(() => import('../pages/EmailVerifiedPage'));

// RochaWMS - Kanban System
const SpacesPage = React.lazy(() => import('../pages/SpacesPage'));
const AreaProjectsPage = React.lazy(() => import('../pages/AreaProjectsPage'));
const ProjectListsPage = React.lazy(() => import('../pages/ProjectListsPage'));
const ListBoardPage = React.lazy(() => import('../pages/ListBoardPage'));
const ReportsTimePage = React.lazy(() => import('../pages/ReportsTimePage'));
const ChartsPage = React.lazy(() => import('../pages/ChartsPage'));
const HoursReportPage = React.lazy(() => import('../pages/HoursReportPage'));
const ReportsSchedulesPage = React.lazy(() => import('../pages/ReportsSchedulesPage'));
const UserHoursDetailPage = React.lazy(() => import('../pages/UserHoursDetailPage'));
const KanbanGlobalPage = React.lazy(() => import('../pages/KanbanGlobalPage'));
const TimeHistoryPage = React.lazy(() => import('../components/time/TimeHistoryPage'));
const ProfilePage = React.lazy(() => import('../pages/ProfilePage'));

// Componente de loading para Suspense
const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
    }}
  >
    <LoadingSpinner size="large" />
  </div>
);

const RouteMemory = () => {
  const location = useLocation();
  const userId = useAuthStore(state => state.user?.id);
  const companyId = useCompanyStore(state => state.companyId);

  useEffect(() => {
    rememberRoute(userId, companyId, `${location.pathname}${location.search}`);
  }, [companyId, location.pathname, location.search, userId]);

  return null;
};

const DefaultRedirect = () => {
  const { can } = usePermissions();
  const userId = useAuthStore(state => state.user?.id);
  const companyId = useCompanyStore(state => state.companyId);

  return <Navigate to={resolveAuthenticatedPath({ userId, companyId, can })} replace />;
};

const AppRoutes = () => {
  return (
    <Router>
      <RouteMemory />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Rotas públicas */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invite/accept" element={<InviteAcceptPage />} />
          <Route path="/email-verified" element={<EmailVerifiedPage />} />

          {/* Rotas protegidas */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <AppShell>
                  <DefaultRedirect />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/profile"
            element={
              <AuthGuard>
                <AppShell>
                  <ProfilePage />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/companies"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="company.view">
                    <CompaniesPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/company-users"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="member.view">
                    <CompanyUsersPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/roles"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="roles.view">
                    <RolesPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          {/* Rota para edição de perfil específico */}
          <Route
            path="/roles/:id"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="roles.view">
                    <RoleEditPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/invitations"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="invitations.view">
                    <InvitationsPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          {/* RochaWMS - Kanban System Routes */}
          <Route
            path="/rocha-wms/spaces"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="areas.view">
                    <SpacesPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/rocha-wms/areas/:areaId/projects"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="project.view">
                    <AreaProjectsPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/rocha-wms/projects/:projectId/lists"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="list.view">
                    <ProjectListsPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/lists/:listId/board"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="list.view">
                    <ListBoardPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/rocha-wms/reports/charts"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="reports.view">
                    <ChartsPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/rocha-wms/reports/time"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="reports.view">
                    <ReportsTimePage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/rocha-wms/reports/schedules"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="reports.view">
                    <ReportsSchedulesPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/rocha-wms/reports/hours"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="reports.view">
                    <HoursReportPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/rocha-wms/reports/hours/:userId"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="reports.view">
                    <UserHoursDetailPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/rocha-wms/kanban"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="list.view">
                    <KanbanGlobalPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/tasks/:taskId/time-history"
            element={
              <AuthGuard>
                <AppShell>
                  <TimeHistoryPage />
                </AppShell>
              </AuthGuard>
            }
          />

          {/* Rota 404 */}
          <Route
            path="*"
            element={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '100vh',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <h1>404 - Página não encontrada</h1>
                <button onClick={() => (window.location.href = '/')}>Voltar ao início</button>
              </div>
            }
          />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;
